import { z } from "zod";

/**
 * Login form schema — RHF + Zod resolver (REQ-UI-005).
 *
 * Validace:
 *   - email — non-empty, valid email format (frontend pre-check; backend dělá
 *     anti-enum response: always 200 pro `/auth/forgot-password`, jen `/auth/login`
 *     vrátí 401 INVALID_CREDENTIALS).
 *   - password — min 14 znaků per Section 6 + REQ-AUTH-002 + UC-011 S2.
 *
 * Note: password policy je primárně vynucena při SET (registration, change),
 * ne při LOGIN — uživatelé s legacy heslem < 14 by viděli vykřičník "expired
 * password, please reset" message. Login schema má proto **min 1** char (jen
 * "non-empty"), backend rozhodne validity.
 */

export const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "E-mail je povinný.")
    .email("Neplatný formát e-mailu."),
  password: z.string().min(1, "Heslo je povinné."),
});

export type LoginInput = z.infer<typeof loginSchema>;

/**
 * Forgot-password schema — POST /api/public/auth/forgot-password
 * (anti-enumeration: server vrátí 200 vždy, i pro neexistující email).
 */
export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "E-mail je povinný.")
    .email("Neplatný formát e-mailu."),
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

/**
 * Reset-password schema — POST /api/public/auth/reset-password/{token}
 * (token přichází z URL path, validuje backend ConsumedToken 1-time-use).
 *
 * Min 14 znaků per REQ-AUTH-002 + UC-011 S2. Confirm match validuje
 * `superRefine`.
 */
export const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(14, "Heslo musí mít alespoň 14 znaků (požadavek bezpečnosti)."),
    confirmPassword: z.string(),
  })
  .superRefine((val, ctx) => {
    if (val.password !== val.confirmPassword) {
      ctx.addIssue({
        code: "custom",
        path: ["confirmPassword"],
        message: "Hesla se neshodují.",
      });
    }
  });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

/**
 * Change-password schema (UC-011 — user self-service password change).
 */
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Aktuální heslo je povinné."),
    newPassword: z
      .string()
      .min(14, "Nové heslo musí mít alespoň 14 znaků."),
    confirmPassword: z.string(),
  })
  .superRefine((val, ctx) => {
    if (val.newPassword !== val.confirmPassword) {
      ctx.addIssue({
        code: "custom",
        path: ["confirmPassword"],
        message: "Nová hesla se neshodují.",
      });
    }
    if (val.currentPassword === val.newPassword) {
      ctx.addIssue({
        code: "custom",
        path: ["newPassword"],
        message: "Nové heslo se musí lišit od aktuálního.",
      });
    }
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;
